import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Destination } from '@/types/iterpec';

const SESSION_ID_KEY = 'search_session_id';

const getOrCreateSessionId = (): string => {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
};

export const useSearchHistory = () => {
  const [recentSearches, setRecentSearches] = useState<Destination[]>([]);
  const [loading, setLoading] = useState(false);

  const loadRecentSearches = async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      // Anonymous users use localStorage-based recents (no server round trip)
      if (!user) {
        try {
          const raw = localStorage.getItem('recent_searches');
          const parsed: Destination[] = raw ? JSON.parse(raw) : [];
          setRecentSearches(parsed.slice(0, 3));
        } catch {
          setRecentSearches([]);
        }
        return;
      }

      const { data, error } = await supabase
        .from('search_history')
        .select('destination_id, destination_name, destination_type')
        .eq('user_id', user.id)
        .order('searched_at', { ascending: false })
        .limit(3);

      if (error) throw error;

      const mapped: Destination[] = (data || []).map((item) => ({
        DestinationId: item.destination_id,
        Name: item.destination_name,
        Type: item.destination_type || '',
      }));

      const seen = new Set<string>();
      const unique = mapped.filter((d) => {
        const key = d.DestinationId;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      setRecentSearches(unique);
    } catch (error) {
      console.error('Error loading recent searches:', error);
      setRecentSearches([]);
    } finally {
      setLoading(false);
    }
  };

  const saveSearch = async (destination: Destination) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        // Persist recents in localStorage for anonymous users
        try {
          const raw = localStorage.getItem('recent_searches');
          const parsed: Destination[] = raw ? JSON.parse(raw) : [];
          const deduped = [destination, ...parsed.filter(d => d.DestinationId !== destination.DestinationId)].slice(0, 10);
          localStorage.setItem('recent_searches', JSON.stringify(deduped));
        } catch {
          // ignore storage errors
        }
        loadRecentSearches();
        return;
      }

      await supabase.from('search_history').insert({
        user_id: user.id,
        session_id: null,
        destination_id: destination.DestinationId,
        destination_name: destination.Name,
        destination_type: destination.Type,
      });

      loadRecentSearches();
    } catch (error) {
      console.error('Error saving search:', error);
    }
  };

  useEffect(() => {
    loadRecentSearches();
  }, []);

  return { recentSearches, saveSearch, loading };
};
